import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Zap, Moon, Sun, Bell, BellOff, Monitor,
  ChevronRight, Check, Loader2, Shield, Trash2, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// eBay region options
const EBAY_REGIONS = [
  { code: "US", name: "United States", flag: "🇺🇸", currency: "USD" },
  { code: "UK", name: "United Kingdom", flag: "🇬🇧", currency: "GBP" },
  { code: "DE", name: "Germany", flag: "🇩🇪", currency: "EUR" },
  { code: "FR", name: "France", flag: "🇫🇷", currency: "EUR" },
  { code: "AU", name: "Australia", flag: "🇦🇺", currency: "AUD" },
  { code: "CA", name: "Canada", flag: "🇨🇦", currency: "CAD" },
  { code: "IT", name: "Italy", flag: "🇮🇹", currency: "EUR" },
  { code: "ES", name: "Spain", flag: "🇪🇸", currency: "EUR" },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, token, loading: authLoading } = useAuth();
  
  // Region state
  const [selectedRegion, setSelectedRegion] = useState(() => {
    // Try to load from localStorage for guests
    return localStorage.getItem('flipvalue_region') || "US";
  });
  const [regionLoading, setRegionLoading] = useState(false);
  
  // Settings state with lazy initialization from localStorage
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('flipvalue_settings');
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (e) {
        console.error('Failed to parse settings:', e);
      }
    }
    return {
      notifications: {
        priceAlerts: true,
        releaseReminders: true,
        weeklyDigest: false
      },
      preferences: {
        compactView: false
      }
    };
  });
  const [saving, setSaving] = useState(false);

  // Load user's region preference on mount
  useEffect(() => {
    const loadUserRegion = async () => {
      if (!token) {
        // Auto-detect region for unauthenticated users if not already set
        const savedRegion = localStorage.getItem('flipvalue_region');
        if (!savedRegion) {
          autoDetectRegion();
        }
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.region) {
            setSelectedRegion(data.region);
          } else {
            // No saved region, auto-detect
            autoDetectRegion();
          }
          // Also load notification preferences from backend
          if (data.notification_preferences) {
            setSettings(prev => ({
              ...prev,
              notifications: {
                ...prev.notifications,
                ...data.notification_preferences
              }
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load user region:', error);
        autoDetectRegion();
      }
    };
    
    loadUserRegion();
  }, [token]);

  // Auto-detect region from browser language
  const autoDetectRegion = async () => {
    const browserLanguage = navigator.language || navigator.userLanguage || "en-US";
    
    try {
      const response = await fetch(`${API_URL}/api/regions/detect?language=${encodeURIComponent(browserLanguage)}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedRegion(data.detected_region);
        localStorage.setItem('flipvalue_region', data.detected_region);
      }
    } catch (error) {
      console.error('Failed to auto-detect region:', error);
      // Default to US on error
      setSelectedRegion("US");
    }
  };

  // Handle region change
  const handleRegionChange = async (newRegion) => {
    setSelectedRegion(newRegion);
    localStorage.setItem('flipvalue_region', newRegion);
    
    if (!token) {
      // Save to localStorage for guests
      toast.success(`Region changed to ${EBAY_REGIONS.find(r => r.code === newRegion)?.name || newRegion}`);
      return;
    }
    
    // Save to backend for authenticated users
    setRegionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ region: newRegion })
      });
      
      if (response.ok) {
        toast.success(`Region changed to ${EBAY_REGIONS.find(r => r.code === newRegion)?.name || newRegion}`);
      } else {
        toast.error('Failed to save region preference');
      }
    } catch (error) {
      console.error('Failed to save region:', error);
      toast.error('Failed to save region preference');
    } finally {
      setRegionLoading(false);
    }
  };

  // Save settings to localStorage and backend
  const saveSettings = async () => {
    setSaving(true);
    
    // Save to localStorage
    localStorage.setItem('flipvalue_settings', JSON.stringify(settings));
    
    // If authenticated, also save notification preferences to backend
    if (token) {
      try {
        await fetch(`${API_URL}/api/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ 
            notification_preferences: settings.notifications 
          })
        });
      } catch (error) {
        console.error('Failed to save notification preferences:', error);
      }
    }
    
    setTimeout(() => {
      toast.success('Settings saved successfully');
      setSaving(false);
    }, 500);
  };

  const updateNotification = (key, value) => {
    setSettings(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [key]: value }
    }));
  };

  const updatePreference = (key, value) => {
    setSettings(prev => ({
      ...prev,
      preferences: { ...prev.preferences, [key]: value }
    }));
  };

  // Wait for auth to load
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  const isGuest = !user;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">FlipValue</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
        <h1 className="text-2xl font-bold mb-6">Settings</h1>

        {/* Region Section - Available to all users */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            eBay Region
          </h2>
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="px-4 py-4">
              <div className="flex items-center gap-3 mb-3">
                <Globe className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Marketplace Region</p>
                  <p className="text-sm text-muted-foreground">Search results will use your selected eBay marketplace</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                {EBAY_REGIONS.map((region) => (
                  <button
                    key={region.code}
                    onClick={() => handleRegionChange(region.code)}
                    disabled={regionLoading}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                      selectedRegion === region.code
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50 hover:bg-secondary/50'
                    }`}
                  >
                    <span className="text-lg">{region.flag}</span>
                    <div className="text-left">
                      <p className="text-xs font-medium">{region.code}</p>
                      <p className="text-[10px] text-muted-foreground">{region.currency}</p>
                    </div>
                    {selectedRegion === region.code && (
                      <Check className="w-3 h-3 ml-auto" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Currently using: <span className="font-medium">{EBAY_REGIONS.find(r => r.code === selectedRegion)?.name || selectedRegion}</span>
                {isGuest && <span className="ml-2 text-primary">(Sign in to save your preference)</span>}
              </p>
            </div>
          </div>
        </section>

        {/* Appearance Section - Available to all users */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Appearance
          </h2>
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <button
              onClick={toggleTheme}
              className="w-full px-4 py-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Sun className="w-5 h-5 text-muted-foreground" />
                )}
                <div className="text-left">
                  <p className="font-medium">Theme</p>
                  <p className="text-sm text-muted-foreground">
                    {theme === 'dark' ? 'Dark mode' : 'Light mode'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground capitalize">{theme}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </button>
          </div>
        </section>

        {/* Notifications Section */}
        {isGuest ? (
          <section className="mb-6">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Notifications
            </h2>
            <div className="bg-card rounded-xl border border-border overflow-hidden p-6 text-center">
              <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="font-medium mb-1">Sign in to manage notifications</p>
              <p className="text-sm text-muted-foreground mb-4">Get price alerts, release reminders, and more</p>
              <Button size="sm" onClick={() => navigate('/auth?mode=signin')}>
                Sign In
              </Button>
            </div>
          </section>
        ) : (
          <section className="mb-6">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Notifications
            </h2>
            <div className="bg-card rounded-xl border border-border overflow-hidden divide-y divide-border">
              <div className="px-4 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Price Alerts</p>
                    <p className="text-sm text-muted-foreground">Get notified when inventory items hit your target price</p>
                  </div>
                </div>
                <button
                  onClick={() => updateNotification('priceAlerts', !settings.notifications.priceAlerts)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    settings.notifications.priceAlerts ? 'bg-primary' : 'bg-secondary'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    settings.notifications.priceAlerts ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              <div className="px-4 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Release Reminders</p>
                    <p className="text-sm text-muted-foreground">Get reminded 1 week, 24h, 1h, and 15min before releases</p>
                  </div>
                </div>
                <button
                  onClick={() => updateNotification('releaseReminders', !settings.notifications.releaseReminders)}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    settings.notifications.releaseReminders ? 'bg-primary' : 'bg-secondary'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    settings.notifications.releaseReminders ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              <div className="px-4 py-4 flex items-center justify-between opacity-60">
                <div className="flex items-center gap-3">
                  <BellOff className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Weekly Digest</p>
                    <p className="text-sm text-muted-foreground">Summary of your activity</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-medium bg-secondary rounded-full">Coming Soon</span>
                  </div>
                </div>
                <button
                  disabled
                  className="w-11 h-6 rounded-full bg-secondary cursor-not-allowed"
                >
                  <div className="w-5 h-5 rounded-full bg-white shadow-sm translate-x-0.5" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Preferences Section - Available to all users */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Preferences
          </h2>
          <div className="bg-card rounded-xl border border-border overflow-hidden divide-y divide-border">
            <div className="px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Monitor className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Compact View</p>
                  <p className="text-sm text-muted-foreground">Show more items on screen</p>
                </div>
              </div>
              <button
                onClick={() => updatePreference('compactView', !settings.preferences.compactView)}
                className={`w-11 h-6 rounded-full transition-colors ${
                  settings.preferences.compactView ? 'bg-primary' : 'bg-secondary'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                  settings.preferences.compactView ? 'translate-x-5' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        </section>

        {/* Data Section */}
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Data
          </h2>
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <button
              onClick={() => {
                if (confirm('Are you sure you want to clear all local data? This cannot be undone.')) {
                  localStorage.clear();
                  toast.success('Local data cleared');
                  window.location.reload();
                }
              }}
              className="w-full px-4 py-4 flex items-center justify-between hover:bg-red-500/10 transition-colors text-red-500"
            >
              <div className="flex items-center gap-3">
                <Trash2 className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-medium">Clear Local Data</p>
                  <p className="text-sm opacity-70">Remove cached data from this device</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* Save Button */}
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="w-full h-12"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </main>

      <Footer />
    </div>
  );
}
